import * as React from 'react';
import { useSelector, useStore } from 'react-redux';
import * as Ably from 'ably';

import { HUB_BACKEND_ENVIRONMENTS, HubBackendEnvironmentUrls } from '~/main/environments';
import { CFD_EVENTS } from '~/main/namespace';
import { LoggerScope } from '~/main/utils/logger';
import { cfdActions } from '~/renderer/store/cfd';
import { connectionActions } from '~/renderer/store/connection';
import { getIsConnected } from '~/renderer/store/connection/selectors';
import { CONNECTION_STATUS, ConnectionError, MEMBER_TYPE } from '~/renderer/store/connection/types';
import { getHubBackendEnvironmentUrl } from '~/renderer/store/hub/selectors';
import { isBrowserCFD, isRenderProcess } from '~/renderer/utils';
import Logger from '~/renderer/utils/logger';
import { HUB_MESSAGES } from '~/types/socket';

import { useTracePrintExecution } from '..';
import { useNetworkChange } from '../use-network-change';
import { socketActionCreator } from './actions';
import { CFD_MESSAGES, PRINTER_MESSAGES, Publish } from './types';

import type { ConnectionState } from '~/renderer/store/connection/types';
import type { AppState } from '~/renderer/store/types';

const DISCONNECT_TIMEOUT_DELAY = 1000 * 10;

export const useSetupAbly = () => {
  const [client] = React.useState<Ably.Realtime | null>(null);
  const [publish, setPublish] = React.useState<Publish | null>(null);
  const store = useStore();
  const hubBackendEnvironmentUrl = useSelector(getHubBackendEnvironmentUrl);

  const { channelName } = useSelector<AppState, ConnectionState>(state => state.connection);
  useTracePrintExecution();

  useNetworkChange(client);

  const isMemberConnected = (
    member: MEMBER_TYPE,
    members: Ably.Types.PresenceMessage[] | undefined,
  ) => {
    return members?.some(({ clientId }) => clientId === member);
  };

  React.useEffect(() => {
    if (!channelName) return;

    let baseAuthUrl = hubBackendEnvironmentUrl || process.env.HUB_BACKEND_URL;
    if (
      !hubBackendEnvironmentUrl &&
      !isRenderProcess &&
      (window.location.hostname.includes('lightspeedappstaging') ||
        window.location.hostname.includes('lightspeedappstg'))
    ) {
      // Enable Staging CFD static assets to connect to Staging hub-backend
      baseAuthUrl = HubBackendEnvironmentUrls[HUB_BACKEND_ENVIRONMENTS.STAGING];
    }

    const client = new Ably.Realtime({
      clientId: isRenderProcess ? MEMBER_TYPE.HUB : MEMBER_TYPE.CFD,
      log: {
        level: 2,
        handler: message => {
          const [, , newMessage] = message.match(/(Ably:\s+)(.+)/) || [];
          return Logger({ scope: LoggerScope.ABLY }).debug({
            msg: newMessage || message,
          });
        },
      },
      authUrl: `${baseAuthUrl}api/socket/auth`,
    });
    const channel = client?.channels.get(`private:${channelName}`);

    function publishToAbly(message: string, messageData?: Record<string, unknown> | string) {
      channel?.presence.get((errorInfo, members) => {
        if (errorInfo) {
          handleError(errorInfo);
          return;
        }

        // If the Hub is the only member of the channel, do not send messages on the channel
        if (members?.length === 1 && members[0]?.clientId === MEMBER_TYPE.HUB) {
          Logger({ scope: LoggerScope.ABLY }).debug({
            msg: `HUB is alone in channel: ${channel}`,
            message,
          });
          return false;
        }

        Logger({ scope: LoggerScope.ABLY }).debug({
          msg: `Publish`,
          message,
          messageData,
        });

        channel?.publish(message, messageData);
        return true;
      });
    }
    const actions = socketActionCreator(publishToAbly, store, LoggerScope.ABLY);

    client.connection.once(CONNECTION_STATUS.CONNECTED, onceConnected);
    client.connection.on(CONNECTION_STATUS.CONNECTED, onConnected);
    client.connection.on(CONNECTION_STATUS.DISCONNECTED, onDisconnect);
    client.connection.on('suspended', onError);
    client.connection.on('failed', onError);
    client.connection.on('closed', onDisconnect);
    channel.subscribe(CFD_MESSAGES.SALE_UPDATED, actions.onSaleUpdated);
    channel.subscribe(CFD_MESSAGES.TIP_FLOW_START, actions.onStartTipFlow);
    channel.subscribe(CFD_MESSAGES.SALE_COMPLETED, actions.onSaleCompleted);
    channel.subscribe(CFD_MESSAGES.HIDE_SALE, actions.onHideSale);
    channel.subscribe(CFD_MESSAGES.SHOW_SALE, actions.onShowSale);
    channel.subscribe(CFD_MESSAGES.CUSTOMER_START, actions.onCustomerStart);
    channel.subscribe(CFD_EVENTS.CREATE_CFD, actions.onNewCFD);
    channel.subscribe(CFD_MESSAGES.CLOSE, actions.onCFDClose);
    channel.subscribe(HUB_MESSAGES.CONFIG_UPDATED, actions.onConfigUpdated);
    channel.subscribe(CFD_MESSAGES.COMPLETE_PAYMENT, actions.onTerminalPaymentApproved);
    channel.subscribe(HUB_MESSAGES.DISCONNECT, onRetailDisconnect);

    if (!isBrowserCFD) {
      // Browser cfd doesn't need to subscribe to these messages
      channel.subscribe(PRINTER_MESSAGES.PRINT, actions.onPrint);
      channel.subscribe(PRINTER_MESSAGES.OPEN_CASH_DRAWER, actions.onOpenCashDrawer);
    }

    let shouldWait: boolean = true;
    let shouldGetConfig: boolean = true;
    let disconnectTimeout: number;
    function onPresenceMembersUpdated() {
      const { isBrowserCFDConnected } = store.getState().cfd;
      window.clearTimeout(disconnectTimeout);
      channel?.presence.get((errorInfo, members) => {
        if (errorInfo) {
          handleError(errorInfo);
          return;
        }

        Logger({ scope: LoggerScope.ABLY }).debug({
          msg: 'Presence members updated',
          members,
        });

        /**
         * Glitch when Retail is in the channel before this client.
         * Retail signals its presence triggering onPresenceMembersUpdated.
         * Then this client signals its presence re-triggering onPresenceMembersUpdated
         */
        if (members?.length === 1 && members[0]?.clientId === MEMBER_TYPE.Retail) return;
        /**
         * Handles the case where we have a Browser-enabled CFD connected
         */
        if (isMemberConnected(MEMBER_TYPE.CFD, members) && !isBrowserCFDConnected) {
          store.dispatch(cfdActions.updateBrowserCFDConnection(true));
        }
        /**
         * Handles the case where the Browser-connected CFD disconnects
         */
        if (!isMemberConnected(MEMBER_TYPE.CFD, members) && isBrowserCFDConnected) {
          store.dispatch(cfdActions.updateBrowserCFDConnection(false));
        }
        if (isMemberConnected(MEMBER_TYPE.Retail, members)) {
          shouldWait = true;
          actions.setConnected();
          actions.onConnectedToRetail(shouldGetConfig);
          shouldGetConfig = false;
        } else if (shouldWait) {
          Logger({ scope: LoggerScope.ABLY }).debug({
            msg: 'Presence members updated - should wait',
          });
          disconnectTimeout = window.setTimeout(() => {
            shouldWait = false;
            shouldGetConfig = true;
            onPresenceMembersUpdated();
          }, DISCONNECT_TIMEOUT_DELAY);
        } else {
          shouldWait = true;
          if (getIsConnected(store.getState())) {
            Logger({ scope: LoggerScope.ABLY }).debug({
              msg: 'Presence members updated - CFD disconnect',
            });
            window.ipcRenderer?.send(CFD_EVENTS.NOTIFY_CFD_DISCONNECT);
          }
          actions.setReady();
        }
      });
    }

    function onError({ current, reason }: Ably.Types.ConnectionStateChange) {
      if (reason) {
        handleError(reason);
      }
      store.dispatch(
        connectionActions.updateConnectionError({
          state: current || 'unknown',
          message: reason?.message,
          code: reason?.code,
          statusCode: reason?.statusCode,
          // reason.cause is documented on the ably website, but not included in their types files
          // https://www.ably.io/documentation/realtime/types#error-info
          cause: (reason as Ably.Types.ConnectionStateChange['reason'] & { cause: string }).cause,
        }),
      );

      actions.setDisconnected(reason as ConnectionError);
    }

    function onceConnected() {
      actions.setReady();
      channel?.presence.subscribe('enter', onPresenceMembersUpdated);
      channel?.presence.subscribe('leave', onPresenceMembersUpdated);
    }

    function onConnected(event: Ably.Types.ConnectionStateChange) {
      Logger({ scope: LoggerScope.ABLY }).debug({
        msg: 'Ably connected',
        ...event,
      });
      channel?.attach(() => {
        channel?.history({ untilAttach: true }, (_err, resultPage) => {
          const messageTypes = {
            [CFD_MESSAGES.HIDE_SALE]: actions.onHideSale,
            [CFD_MESSAGES.SHOW_SALE]: actions.onShowSale,
            [CFD_MESSAGES.SALE_COMPLETED]: actions.onSaleCompleted,
            [CFD_MESSAGES.SALE_UPDATED]: actions.onSaleUpdated,
          };

          if (resultPage) {
            for (const item of resultPage.items) {
              if (messageTypes[item.name as keyof typeof messageTypes]) {
                messageTypes[item.name as keyof typeof messageTypes](item);
                return;
              }
            }
          }
        });
      });

      channel?.presence.enter();
      channel?.presence.get(onPresenceMembersUpdated);
    }

    function onDisconnect(event: Ably.Types.ConnectionStateChange) {
      Logger({ scope: LoggerScope.ABLY }).debug({
        msg: 'Ably disconnected',
        ...event,
      });

      const { reason } = event;
      actions.setDisconnected(reason as ConnectionError);
    }

    function onRetailDisconnect() {
      shouldWait = false;
      actions.setReady();
    }

    setPublish(() => publishToAbly);

    return () => {
      client?.close();
      channel?.presence.leave();
      channel?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelName]);

  return publish;
};

function handleError(errorInfo: Ably.Types.ErrorInfo) {
  if (errorInfo.code === 400 || errorInfo.code === 401) {
    Logger({ scope: LoggerScope.APP }).debug({
      msg: 'Ably presence get error',
      errorInfo,
    });
    return;
  }

  Logger({ scope: LoggerScope.APP }).error({
    msg: errorInfo.message,
    err: errorInfo,
  });
}
